import { graphql } from 'gql.tada';
import { useQuery } from '@apollo/client';
import { useEffect } from 'react';

const FamilyDetailQuery = graphql.persisted(
  "FamilyDetailQuery",
  graphql(`
    query FamilyDetailQuery($schoolId: ID!, $familySlug: String!, $schoolYearId: ID!) {
      school(id: $schoolId) {
        id
        name
        slug
        checklistEnrollmentOn
        gradeLevelsSetting {
          items {
            description
            order
          }
        }
        family(slug: $familySlug) {
          id
          name
          slug
          emergencyContacts {
            name
            phone
            relationship
          }
          authorizedPickup {
            name
            phone
            relationship
          }
          churchAffiliation {
            churchName
            pastorName
            churchEmail
          }
          address {
            street
            street2
            city
            state
            zip
            country
          }
          pendingHouseholdChangeRequest {
            id
            submittedAt
            submittedByName
            items {
              key
              label
              currentValue
              proposedValue
              changedSinceSubmitted
            }
          }
          guardians {
            person {
              id
              firstName
              lastName
              email
              phone
              gender
              hasUserAccount
              accountInviteStatus
              accountInviteEmail
              accountInviteExpiresAt
              accountInviteNoticeStatus
              accountInviteNoticeReason
              accountInviteNoticeAt
              accountInviteNoticeOutcomeUnknown
            }
            role
            isPrimary
            receivesEmails
          }
          children {
            person {
              id
              firstName
              lastName
              fullName
              dateOfBirth
              gender
              studentEnrollmentForYear(schoolYearId: $schoolYearId) {
                id
                state
                enrollmentKind
                gradeLevel
                needsReview
              }
              studentProfile {
                id
                dateOfBirth
                medicalInfo {
                  medicalCondition
                  otcMedicationPermission
                  otcMedicationsApproved {
                    acetaminophen
                    ibuprofen
                    coughDrops
                    other
                  }
                  specialNeeds
                  insuranceInfo {
                    carrier
                    groupNumber
                    policyNumber
                    policyHolderName
                  }
                }
                adminMetadata {
                  academicProbation
                  behavioralProbation
                }
              }
            }
            role
          }
          familyEnrollmentForYear(schoolYearId: $schoolYearId) {
            id
            schoolYear {
              id
              startDate
              endDate
              enrollmentFeeSetting { amountCents }
              tuitionOptionsSetting { items { key name costCents } }
              tuitionPaymentOptionsSetting {
                key
                name
                scheduleType
                selectableOnEnrollment
                startMonth
                billingDayOfMonth
                discountCents
                discountType
              }
            }
            enrollmentKind
            state
            access
            accessSource
            canDelete
            inviteStatus
            isInquiry
            inviteToken
            studentCount
            canQueueForInvite
            canSendInvite
            additionalPaymentOptionKeys
            metadata
            enrollmentFee { kind amountCents }
            checklist {
              id
              completedAt
              completedVia
              deadlineOn
              late
              progress { done total waitingOn }
              items {
                id title round personName state lockedReason stepType required completedAt completedVia adHoc officeOnly completedNote waivedReason keptAsItIs
                feeInvoiceId
                tuitionAgreement {
                  agreementId state paymentOptionKey aidRequested aidCents aidReason totalCents
                  installments { dueOn amountCents autoPay }
                  kept { dueOn amountCents status }
                  supersedesAgreementId
                  offeredAt signedAt signedName signedMethod
                }
              }
              guardians { name email status expiresAt }
            }
            queuedAt
            sentAt
            submittedAt
            approvedAt
            packetSentAt
            packetViewedAt
            packetSubmittedAt
            createdAt
            updatedAt
            studentEnrollments {
              id
              state
              enrollmentKind
              gradeLevel
              needsReview
              startsOn
              tuitionOptionKey
              familyTuitionOptionKey
              person {
                id
                firstName
                lastName
              }
            }
            invoices {
              id
              key
              status
              checkoutPaymentStatus
              autopayFailureReason
              subscriptionManaged
              dueDate
              amountCents
              memo
              postedAt
              createdAt
              lineItems {
                id
                description
                amountCents
                lineType
                studentEnrollment {
                  id
                  person {
                    id
                    firstName
                    lastName
                  }
                }
              }
            }
          }
        }
      }
    }
  `)
);

export { FamilyDetailQuery };

export function useFamilyDetailQuery(schoolId: string | undefined, familySlug: string | undefined, schoolYearId: string | undefined) {
  const result = useQuery(FamilyDetailQuery, {
    variables: {
      schoolId: schoolId!,
      familySlug: familySlug!,
      schoolYearId: schoolYearId || '',
    },
    skip: !schoolId || !familySlug,
  });
  const awaitingDelivery = result.data?.school?.family?.guardians.some((guardian) => guardian.person.accountInviteNoticeStatus === 'Queued') ?? false;
  const { startPolling, stopPolling } = result;
  useEffect(() => {
    if (awaitingDelivery) startPolling(10_000);
    else stopPolling();
    return stopPolling;
  }, [awaitingDelivery, startPolling, stopPolling]);
  return result;
}
